export type DirectCallTraceStage =
  | "vad"
  | "stt"
  | "transcript_to_llm"
  | "llm"
  | "tts"
  | "full_turn"
  | string;

export interface DirectCallTraceEvent {
  sequence: number;
  type: string;
  stage?: DirectCallTraceStage;
  provider?: string;
  event?: string;
  duration_ms?: number;
  from_event?: string;
  to_event?: string;
  started_at?: string | null;
  completed_at?: string | null;
  timeline_order?: number;
  timeline_label?: string;
  tool_name?: string;
  variables?: Record<string, string | number | boolean | null>;
  processor?: string;
  model?: string;
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  cacheReadInputTokens?: number | null;
  promptCacheState?: string | null;
}

export type DirectCallTraceSeverity = "normal" | "warning" | "critical";

export interface DirectCallTraceTurn {
  turnNumber: number;
  events: DirectCallTraceEvent[];
}

export interface DirectCallTraceTurnSummary {
  vad?: DirectCallTraceEvent;
  stt?: DirectCallTraceEvent;
  transcriptToLlm?: DirectCallTraceEvent;
  llm?: DirectCallTraceEvent;
  tts?: DirectCallTraceEvent;
  fullTurn?: DirectCallTraceEvent;
  tokenUsage?: DirectCallTraceEvent;
  toolCalls: DirectCallTraceEvent[];
}

const STAGE_WARNING_MS: Record<string, number> = {
  stt: 1500,
  transcript_to_llm: 750,
  llm: 3000,
  tts: 1000,
  full_turn: 4500,
  vad: 4000,
};

const STAGE_CRITICAL_MS: Record<string, number> = {
  stt: 3500,
  transcript_to_llm: 2000,
  llm: 8000,
  tts: 2500,
  full_turn: 10000,
  vad: 10000,
};

export const getDirectCallTraceSeverity = (
  stage?: string,
  durationMs?: number
): DirectCallTraceSeverity => {
  if (typeof durationMs !== "number") return "normal";
  const stageKey = stage || "unknown";
  if (durationMs >= (STAGE_CRITICAL_MS[stageKey] ?? 10000)) return "critical";
  if (durationMs >= (STAGE_WARNING_MS[stageKey] ?? 4500)) return "warning";
  return "normal";
};

export const formatDirectCallTraceDuration = (
  durationMs?: number
): string => {
  if (typeof durationMs !== "number") return "-";
  if (durationMs >= 1000) return `${(durationMs / 1000).toFixed(2)}s`;
  return `${Math.round(durationMs)}ms`;
};

export const groupDirectCallTraceEvents = (
  events: DirectCallTraceEvent[]
): DirectCallTraceTurn[] => {
  const sortedEvents = [...events].sort((a, b) => a.sequence - b.sequence);
  const turns: DirectCallTraceTurn[] = [];
  let current: DirectCallTraceTurn | null = null;

  sortedEvents.forEach((event) => {
    const startsWithVad =
      event.type === "vad_activity" && event.event === "user_started_speaking";
    const startsWithStt =
      event.type === "stage_timing" &&
      event.stage === "stt" &&
      !current?.events.some(
        (currentEvent) =>
          currentEvent.type === "vad_activity" &&
          currentEvent.event === "user_started_speaking"
      );

    if ((startsWithVad || startsWithStt) && current?.events.length) {
      current = null;
    }

    if (!current) {
      current = { turnNumber: turns.length + 1, events: [] };
      turns.push(current);
    }

    current.events.push(event);
  });

  return turns;
};

export const summarizeDirectCallTraceTurn = (
  turn: DirectCallTraceTurn
): DirectCallTraceTurnSummary => {
  const tokenUsageEvents = turn.events.filter((event) => event.type === "token_usage");
  const tokenUsage =
    tokenUsageEvents.find((event) => String(event.processor || "").endsWith("#0")) ||
    [...tokenUsageEvents].sort(
      (a, b) => (b.promptTokens || 0) - (a.promptTokens || 0)
    )[0];

  return {
    vad: [...turn.events]
      .reverse()
      .find((event) => event.type === "vad_activity" && typeof event.duration_ms === "number"),
    stt: turn.events.find((event) => event.type === "stage_timing" && event.stage === "stt"),
    transcriptToLlm: turn.events.find(
      (event) => event.type === "stage_timing" && event.stage === "transcript_to_llm"
    ),
    llm: turn.events.find((event) => event.type === "stage_timing" && event.stage === "llm"),
    tts: turn.events.find((event) => event.type === "stage_timing" && event.stage === "tts"),
    fullTurn: turn.events.find(
      (event) => event.type === "stage_timing" && event.stage === "full_turn"
    ),
    tokenUsage,
    toolCalls: turn.events.filter((event) => event.type === "tool_call"),
  };
};

export const formatDirectCallTraceTimestamp = (
  timestamp?: string | null
): string => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const summarizeDirectCallTrace = (events: DirectCallTraceEvent[]) => {
  const stageEvents = events.filter(
    (event) => event.type === "stage_timing" && typeof event.duration_ms === "number"
  );
  const slowestStage = stageEvents.reduce<
    | (DirectCallTraceEvent & { severity: DirectCallTraceSeverity })
    | null
  >((slowest, event) => {
    const eventWithSeverity = {
      ...event,
      severity: getDirectCallTraceSeverity(event.stage, event.duration_ms),
    };
    if (!slowest || (event.duration_ms || 0) > (slowest.duration_ms || 0)) {
      return eventWithSeverity;
    }
    return slowest;
  }, null);

  return {
    totalEvents: events.length,
    turnCount: groupDirectCallTraceEvents(events).length,
    slowestStage,
  };
};
